import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {
  COLORS,
  ContentPopperProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  ProgressBar,
  Text,
  UnstyledButton,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {MovMoreInfoPopup} from './MovMoreInfoPopup';

export interface MovProgressProps {
  ProgressMoreInfoLayoutComponent: ContentPopperProps['LayoutComponent'];
  onMoreInfoIconClick?: VoidFunction;
  title: string;
  subtitle: string;
  progress: number;
  moreInfoTitle: string;
  moreInfoDescription: string;
  padded?: boolean;
  showSubtitleAboveProgressBar?: boolean;
}

export const MovProgress = ({
  ProgressMoreInfoLayoutComponent,
  padded,
  moreInfoDescription,
  moreInfoTitle,
  onMoreInfoIconClick,
  progress,
  subtitle,
  title,
  showSubtitleAboveProgressBar,
  ref,
}: MovProgressProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const {t} = useTranslation();
  const {
    open: openMovMoreInfoPopup,
    isOpen: isMovMoreInfoPopup,
    cancel: closeMovMoreInfoPopup,
  } = useOpenedStateManager('MOV_MORE_INFO_POPUP');

  const handleMoreInfoIconClick = () => {
    onMoreInfoIconClick?.();
    openMovMoreInfoPopup();
  };
  return (
    <>
      <MovMoreInfoPopup
        isOpen={isMovMoreInfoPopup}
        LayoutComponent={ProgressMoreInfoLayoutComponent}
        moreInfoIconText={moreInfoTitle}
        moreInfoIconDescription={moreInfoDescription}
        onClose={closeMovMoreInfoPopup}
      />
      <Wrapper data-testid="shippingCostProgressRow" ref={ref}>
        <HeaderWrapper>
          <TitleWrapper>
            <FreeShippingIcon
              data-padded={padded}
              asset={GenericIconName.UspFreeShipping}
            />

            <Headline data-padded={padded} data-testid="shippingHintTitle">
              {title}
            </Headline>
          </TitleWrapper>
          <UnstyledButton
            data-testid="shippingHintIcon"
            onClick={handleMoreInfoIconClick}
            aria-label={t('open shipping info popup')}
          >
            <Icon asset={GenericIconName.InfoFilledGray} size="XS" />
          </UnstyledButton>
        </HeaderWrapper>
        {showSubtitleAboveProgressBar && (
          <Subline data-padded={padded} data-testid="shippingHintSubtitle">
            {subtitle}
          </Subline>
        )}
        <StyledProgressBar
          total={100}
          count={progress * 100}
          color={COLORS.Green3}
          height={5}
          data-padded={padded}
          animated
        />
        {!showSubtitleAboveProgressBar && (
          <Subline data-padded={padded} data-testid="shippingHintSubtitle">
            {subtitle}
          </Subline>
        )}
      </Wrapper>
    </>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const FreeShippingIcon = styled(Icon)`
  display: none;
  &[data-padded='true'] {
    display: block;
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const Subline = styled(Text)`
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px10};
  font-weight: 500;
  margin-top: 0.25rem;
  &[data-padded='true'] {
    font-size: ${FONT_SIZES.px12};
    color: ${COLORS.Black};
    margin-top: 0.5rem;
    line-height: 1.25rem;
  }
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  &[data-padded='true'] {
    font-size: ${FONT_SIZES.px16};
  }
`;

const StyledProgressBar = styled(ProgressBar)`
  margin-top: 0.5rem;
  &[data-padded='true'] {
    margin-top: 1rem;
  }
`;

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
